import { MAX_CARD_BACK_PIXEL_HEIGHT, MAX_CARD_PIXEL_HEIGHT } from '../../../constants/constants';
import { range } from '../../../utils/utils';
const HEIGHT_TO_WIDTH_RATIO = 3.5 / 2.5;
// The amount (ratio) of vertical space of the card texture that is opaque (there are some transparent pixels around the card).
// To calculate the amount of horizontal space that is opaque, use in combination with HEIGHT_TO_WIDTH_RATIO.
const OPAQUE_TEXTURE_HEIGHT_RATIO = 980 / 1024;
export const DEFAULT_CARD_TEXTURE_SIZE_PX = 512;
const COMMUNITY_CARD_ANGLES = [-5, -2.5, 0, 2.5, 5];
const COMMUNITY_CARD_DELTA_X_RATIOS = [0.06, 0.03, 0, -0.03, -0.06];
const COMMUNITY_CARD_DELTA_Y_RATIOS = [0.05, -0.01, -0.03, -0.01, 0.05];
function getCardSpacingProportionOfWidth(numCards, isShowingCardBack) {
    if (isShowingCardBack) {
        return 0.25;
    }
    switch (numCards) {
        case 4: {
            return 0.75;
        }
        case 3: {
            return 0.85;
        }
        case 2: {
            return 0.9;
        }
    }
    return 0.95;
}
function createAllCardRenderPropertiesInSingleLine(groupContainerW, groupContainerH, numCards, isShowingCardBack, isTopLeftAligned, cardTextureSizePx, opaqueTextureHeightRatio) {
    const unscaledCardHeight = Math.min(isShowingCardBack ? MAX_CARD_BACK_PIXEL_HEIGHT : MAX_CARD_PIXEL_HEIGHT, groupContainerH);
    const unscaledCardWidth = unscaledCardHeight / HEIGHT_TO_WIDTH_RATIO;
    const renderedSpacingW = getCardSpacingProportionOfWidth(numCards, isShowingCardBack) * unscaledCardWidth;
    const leftmostCardRenderedCenterX = (-(numCards - 1) * renderedSpacingW) / 2;
    // "ideal" means "if not constrained by group container's width"
    const idealScale = unscaledCardHeight / (cardTextureSizePx / opaqueTextureHeightRatio);
    const idealRenderedGroupW = unscaledCardWidth + (numCards - 1) * renderedSpacingW;
    // calculate if we need to scale down further (if the container width is not wide enough for the "ideal" size):
    const additionalScaleFactor = idealRenderedGroupW > groupContainerW ? groupContainerW / idealRenderedGroupW : undefined;
    const scaleFullPrecision = additionalScaleFactor !== undefined ? idealScale * additionalScaleFactor : idealScale;
    // reduce precision slightly for better readability in unit tests:
    const scale = Number(scaleFullPrecision.toFixed(3));
    const allCardRenderProperties = range(numCards).map((i) => {
        const idealX = leftmostCardRenderedCenterX + i * renderedSpacingW;
        let x = additionalScaleFactor !== undefined ? additionalScaleFactor * idealX : idealX;
        let y = 0;
        let angle;
        if (numCards === 5) {
            // shift community cards a bit to look cooler
            x += COMMUNITY_CARD_DELTA_X_RATIOS[i] * unscaledCardHeight;
            y += COMMUNITY_CARD_DELTA_Y_RATIOS[i] * unscaledCardHeight;
            angle = COMMUNITY_CARD_ANGLES[i];
        }
        return {
            x,
            y,
            scale,
            renderedCardHeight: Number((scale * DEFAULT_CARD_TEXTURE_SIZE_PX * OPAQUE_TEXTURE_HEIGHT_RATIO).toFixed(3)),
            angle
        };
    });
    if (isTopLeftAligned) {
        // move cards to top-left of container
        const minCardCenterX = -groupContainerW / 2 + scale * (cardTextureSizePx / HEIGHT_TO_WIDTH_RATIO / 2);
        const minCardCenterY = -groupContainerH / 2 + scale * (cardTextureSizePx / 2);
        const deltaX = allCardRenderProperties[0].x - minCardCenterX;
        const deltaY = allCardRenderProperties[0].y - minCardCenterY;
        return allCardRenderProperties.map((cardRenderProperties) => ({
            ...cardRenderProperties,
            x: cardRenderProperties.x - deltaX,
            y: cardRenderProperties.y - deltaY
        }));
    }
    return allCardRenderProperties;
}
export function createAllCardRenderProperties(groupContainerW, groupContainerH, numCards, { isShowingCardBack, isTopLeftAligned, cardTextureSizePx = DEFAULT_CARD_TEXTURE_SIZE_PX, opaqueTextureHeightRatio = OPAQUE_TEXTURE_HEIGHT_RATIO } = {}) {
    const allSingleLineCardRenderProperties = createAllCardRenderPropertiesInSingleLine(groupContainerW, groupContainerH, numCards, isShowingCardBack, isTopLeftAligned, cardTextureSizePx, opaqueTextureHeightRatio);
    if (numCards === 4) {
        // see if splitting into two lines of cards yields larger cards:
        const testContainerW = groupContainerW;
        const testContainerH = groupContainerH * 0.55;
        const allTopLineCardRenderProperties = createAllCardRenderPropertiesInSingleLine(testContainerW, testContainerH, 2, isShowingCardBack, isTopLeftAligned, cardTextureSizePx, opaqueTextureHeightRatio);
        if (allTopLineCardRenderProperties[0].scale > allSingleLineCardRenderProperties[0].scale) {
            // splitting into two lines yields larger cards, so do that:
            const ySpacing = allTopLineCardRenderProperties[0].scale * cardTextureSizePx * 0.9;
            if (isTopLeftAligned) {
                const deltaY = groupContainerH / 2 - testContainerH / 2;
                return [
                    ...allTopLineCardRenderProperties.map((topLineCardRenderProperties) => ({
                        ...topLineCardRenderProperties,
                        y: topLineCardRenderProperties.y - deltaY
                    })),
                    ...allTopLineCardRenderProperties.map((topLineCardRenderProperties) => ({
                        ...topLineCardRenderProperties,
                        y: topLineCardRenderProperties.y - deltaY + ySpacing
                    }))
                ];
            }
            else {
                // centered
                const absYOffsetFromMiddle = ySpacing / 2;
                return [
                    ...allTopLineCardRenderProperties.map((topLineCardRenderProperties) => ({
                        ...topLineCardRenderProperties,
                        y: -absYOffsetFromMiddle
                    })),
                    ...allTopLineCardRenderProperties.map((topLineCardRenderProperties) => ({
                        ...topLineCardRenderProperties,
                        y: absYOffsetFromMiddle
                    }))
                ];
            }
        }
    }
    return allSingleLineCardRenderProperties;
}
